import { DashboardChartEmpty } from '@/pages/dashboard/components/DashboardChartEmpty'
import { DashboardChartHost } from '@/pages/dashboard/components/DashboardChartHost'
import {
	baseChartOptions,
	chartDatetimeRangePadding,
	chartTheme,
	denseDateAxisLabels,
} from '@/pages/dashboard/components/dashboardTheme'
import { formatMoney } from '@/pages/dashboard/utils/transactionChartUtils'
import type { ApexOptions } from 'apexcharts'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'

export type TrendPoint = {
	x: number
	label: string
	volume: number
	revenue: number
}

type VolumeRevenueTrendChartProps = {
	points: TrendPoint[]
	currency: string
	loading?: boolean
	height?: number
}

const REVENUE_COLOR = '#22c55e'
const VOLUME_COLOR = '#818cf8'

function sum(values: number[]) {
	return values.reduce((a, b) => a + b, 0)
}

export function VolumeRevenueTrendChart({ points, currency, loading, height = 340 }: VolumeRevenueTrendChartProps) {
	const hasData = points.some((p) => p.volume > 0 || p.revenue > 0)

	const totals = useMemo(
		() => ({
			volume: sum(points.map((p) => p.volume)),
			revenue: sum(points.map((p) => p.revenue)),
		}),
		[points]
	)

	const maxVolume = useMemo(() => Math.max(...points.map((p) => p.volume), 0), [points])
	const maxRevenue = useMemo(() => Math.max(...points.map((p) => p.revenue), 0), [points])

	const dayCount = points.length
	const tickAmount = dayCount <= 10 ? dayCount : dayCount <= 31 ? 8 : 10
	const crowdedLabels = dayCount > 14
	const xValues = useMemo(() => points.map((p) => p.x), [points])

	const chartOptions: ApexOptions = useMemo(
		() => ({
			chart: {
				...baseChartOptions,
				type: 'line',
				height,
				animations: { enabled: true, easing: 'easeinout', speed: 400 },
			},
			theme: { mode: 'dark' },
			colors: [VOLUME_COLOR, REVENUE_COLOR],
			stroke: {
				width: [0, 2.5],
				curve: 'smooth',
			},
			plotOptions: {
				bar: {
					borderRadius: 4,
					columnWidth: dayCount > 20 ? '70%' : dayCount > 10 ? '55%' : '42%',
				},
			},
			dataLabels: { enabled: false },
			grid: {
				borderColor: chartTheme.borderColor,
				strokeDashArray: 3,
				padding: { left: 12, right: 24, top: 4, bottom: crowdedLabels ? 4 : 0 },
				xaxis: { lines: { show: false } },
				yaxis: { lines: { show: true } },
			},
			xaxis: {
				type: 'datetime',
				tickAmount,
				...chartDatetimeRangePadding(xValues, 0.06),
				labels: denseDateAxisLabels(dayCount),
				axisBorder: { show: false },
				axisTicks: { show: false },
				crosshairs: {
					stroke: { color: chartTheme.borderColor, width: 1, dashArray: 4 },
				},
				tooltip: { enabled: false },
			},
			yaxis: [
				{
					seriesName: 'Transactions',
					title: {
						text: 'Transactions',
						style: { color: VOLUME_COLOR, fontSize: '11px', fontWeight: 600 },
					},
					min: 0,
					max: maxVolume <= 1 ? 4 : Math.ceil(maxVolume * 1.25),
					tickAmount: maxVolume <= 1 ? 4 : 5,
					forceNiceScale: maxVolume > 1,
					labels: {
						style: { colors: chartTheme.foreColor, fontSize: '11px' },
						formatter: (v) => Math.round(v).toString(),
					},
				},
				{
					seriesName: 'Revenue',
					opposite: true,
					title: {
						text: `Revenue (${currency.toUpperCase()})`,
						style: { color: REVENUE_COLOR, fontSize: '11px', fontWeight: 600 },
					},
					min: 0,
					max: maxRevenue <= 0 ? 100 : undefined,
					forceNiceScale: true,
					labels: {
						style: { colors: chartTheme.foreColor, fontSize: '11px' },
						formatter: (v) => {
							if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
							if (v >= 1000) return `${(v / 1000).toFixed(1)}k`
							return v.toFixed(0)
						},
					},
				},
			],
			fill: {
				type: ['solid', 'gradient'],
				opacity: [0.85, 1],
				gradient: {
					shade: 'dark',
					type: 'vertical',
					shadeIntensity: 0.35,
					opacityFrom: 0.45,
					opacityTo: 0.05,
					stops: [0, 85, 100],
				},
			},
			markers: {
				size: maxVolume <= 3 ? [4, 0] : [0, 0],
				strokeWidth: 0,
				hover: { size: 6, sizeOffset: 2 },
			},
			tooltip: {
				shared: true,
				intersect: false,
				theme: 'dark',
				x: { format: 'EEE, MMM d, yyyy' },
				custom: ({ series, dataPointIndex }) => {
					const pt = points[dataPointIndex]
					if (!pt) return ''
					const vol = series[0]?.[dataPointIndex] ?? 0
					const rev = series[1]?.[dataPointIndex] ?? 0
					return `
						<div style="padding:10px 12px;font-size:12px;line-height:1.5">
							<div style="font-weight:600;margin-bottom:8px">${pt.label}</div>
							<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
								<span style="width:8px;height:8px;border-radius:50%;background:${VOLUME_COLOR}"></span>
								<span style="opacity:0.75">Transactions</span>
								<span style="margin-left:auto;font-weight:600">${Math.round(vol)}</span>
							</div>
							<div style="display:flex;align-items:center;gap:8px">
								<span style="width:8px;height:8px;border-radius:50%;background:${REVENUE_COLOR}"></span>
								<span style="opacity:0.75">Revenue</span>
								<span style="margin-left:auto;font-weight:600">${formatMoney(rev, currency)}</span>
							</div>
						</div>
					`
				},
			},
			legend: { show: false },
		}),
		[currency, crowdedLabels, dayCount, height, maxRevenue, maxVolume, points, tickAmount, xValues]
	)

	const series = useMemo(
		() => [
			{
				name: 'Transactions',
				type: 'column' as const,
				data: points.map((p) => ({ x: p.x, y: p.volume })),
			},
			{
				name: 'Revenue',
				type: 'area' as const,
				data: points.map((p) => ({ x: p.x, y: p.revenue })),
			},
		],
		[points]
	)

	if (loading) {
		return <div className="flex h-[340px] items-center justify-center text-sm text-muted-foreground">Loading chart…</div>
	}

	if (!hasData) {
		return <DashboardChartEmpty message="No transactions in this date range. Try a wider range or different dates." />
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-wrap gap-2">
					<span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/30 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
						<span className="h-2 w-2 rounded-full" style={{ background: VOLUME_COLOR }} />
						Transactions
						<span className="text-foreground">{totals.volume.toLocaleString()}</span>
					</span>
					<span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/30 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
						<span className="h-2 w-2 rounded-full" style={{ background: REVENUE_COLOR }} />
						Revenue
						<span className="text-foreground">{formatMoney(totals.revenue, currency)}</span>
					</span>
				</div>
				<span className="text-[11px] text-muted-foreground">{dayCount} days</span>
			</div>

			<DashboardChartHost>
				<ReactApexChart options={chartOptions} series={series} type="line" height={height} width="100%" />
			</DashboardChartHost>
		</div>
	)
}
